import { cva, type VariantProps } from 'class-variance-authority';

/**
 * PageSection variant styles using Class Variance Authority (CVA)
 *
 * Defines visual variants and alignment options for the PageSection component.
 * All styles use design tokens from the HEN Design System.
 *
 * ## Variants
 * - **light** (default): Light gray background (bg-neutral-50) - Subtle separation
 * - **white-outline**: White background with border (bg-white border-neutral-200) - Defined separation
 *
 * ## Alignment Options
 * - **center** (default): Center-aligned text - Best for featured content and hero sections
 * - **left**: Left-aligned text - Best for content-heavy sections and reading flow
 * - **right**: Right-aligned text - Best for creative layouts and special emphasis
 *
 * ## Base Styles
 * - Flex column layout with gap-2xl for spacing
 * - Rounded corners (rounded-xl) for modern appearance
 * - Section padding using design tokens (px-section-sides, py-section-top-bottom)
 * - Items aligned to end for consistent content positioning
 */
export const pageSectionVariants = cva(
  'flex flex-col rounded-xl gap-2xl px-section-sides py-section-top-bottom',
  {
    variants: {
      variant: {
        light: 'bg-neutral-50',
        'white-outline': 'bg-white border border-neutral-200',
      },
      alignment: {
        center: 'text-center',
        left: 'text-left',
        right: 'text-right',
      },
    },
    defaultVariants: {
      variant: 'light',
    },
  },
);

export type PageSectionVariantProps = VariantProps<typeof pageSectionVariants>;
